import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Upload, Plus, Trash2, FileText } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = {
  // Editable section headings
  hero_eyebrow: "About Manik Engineering",
  hero_line1: "Engineering the",
  hero_line2: "spaces where India works.",
  md_eyebrow: "Managing Director",
  story_eyebrow: "Our Story",
  story_heading: "Building the workspaces where industry gets things done — since 1973.",
  values_heading: "Our Values",
  policy_eyebrow: "Our Policy",
  awards_eyebrow: "Awards & Accolades",
  awards_heading: "Recognised for the work.",
  patents_eyebrow: "Intellectual Property",
  patents_heading: "Ideas we own.",
  // Content
  md_name: "Mr. Kishin Manik",
  md_role: "Managing Director",
  md_quote: "",
  md_photo: "",
  policy_title: "Our Quality Policy",
  policy_body: "",
  policy_image: "",
  story_p1: "",
  story_p2: "",
  story_p3: "",
  awards: [],
  patents: [],
  // Contact
  questionnaire_url: "/assets/inplant-questionnaire.pdf",
};

function ImageField({ label, value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded.");
    } catch {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">{label}</Label>
      <div className="mt-2 flex items-center gap-4">
        {value ? (
          <div className="relative w-24 h-24 border border-black/10 overflow-hidden shrink-0">
            <img src={resolveImg(value)} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="w-24 h-24 border border-dashed border-black/25 flex items-center justify-center text-black/25 shrink-0 text-xs">
            No image
          </div>
        )}
        <div className="flex-1">
          <Input
            data-testid={`${testid}-url`}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="rounded-none border-black/15 h-11"
            placeholder="Image URL, /assets/... or upload a file"
          />
          <label className="mt-2 inline-flex items-center gap-2 border border-black/15 px-3 py-2 cursor-pointer hover:border-[#3da8d8] font-mono-accent text-[10px] uppercase tracking-widest text-[#334155]">
            <Upload size={12} /> {uploading ? "Uploading…" : "Replace"}
            <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`${testid}-file`} />
          </label>
        </div>
      </div>
    </div>
  );
}

function ListEditor({ label, items, onChange, testid, colorClass = "text-[#78be27]" }) {
  const add = () => onChange([...(items || []), { title: "", year: "", note: "", image: "" }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const set = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <Label className={`font-mono-accent text-[10px] uppercase tracking-widest ${colorClass}`}>{label}</Label>
        <button
          type="button"
          onClick={add}
          data-testid={`${testid}-add`}
          className="inline-flex items-center gap-1 border border-black/15 px-3 py-1.5 font-mono-accent text-[10px] uppercase tracking-widest text-[#334155] hover:border-[#3da8d8]"
        >
          <Plus size={12} /> Add
        </button>
      </div>
      <div className="space-y-3">
        {(items || []).map((it, i) => (
          <div key={i} data-testid={`${testid}-row-${i}`} className="border border-black/10 bg-[#F9FAFB] p-4">
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Input
                  value={it.title || ""}
                  onChange={(e) => set(i, "title", e.target.value)}
                  placeholder="Title"
                  className="rounded-none border-black/15 h-10"
                  data-testid={`${testid}-title-${i}`}
                />
              </div>
              <div>
                <Input
                  value={it.year || ""}
                  onChange={(e) => set(i, "year", e.target.value)}
                  placeholder="Year / status"
                  className="rounded-none border-black/15 h-10"
                  data-testid={`${testid}-year-${i}`}
                />
              </div>
            </div>
            <div className="mt-3 flex items-start gap-3">
              <Textarea
                value={it.note || ""}
                onChange={(e) => set(i, "note", e.target.value)}
                placeholder="Short description"
                className="rounded-none border-black/15 min-h-[70px] flex-1"
                data-testid={`${testid}-note-${i}`}
              />
              <button
                type="button"
                onClick={() => remove(i)}
                data-testid={`${testid}-delete-${i}`}
                className="w-9 h-9 border border-black/15 flex items-center justify-center text-[#334155] hover:border-red-500 hover:text-red-600 transition-colors shrink-0"
              >
                <Trash2 size={14} />
              </button>
            </div>
            <div className="mt-3">
              <ImageField
                label="Certificate / Photo (opens in a lightbox on the site)"
                value={it.image}
                onChange={(v) => set(i, "image", v)}
                testid={`${testid}-image-${i}`}
              />
            </div>
          </div>
        ))}
        {(!items || items.length === 0) && (
          <p className="text-xs text-[#52525B] italic">No entries yet — click Add to create one.</p>
        )}
      </div>
    </div>
  );
}

function QuestionnaireField({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/pdf/i.test(file.type) && !/\.pdf$/i.test(file.name)) {
      toast.error("Please choose a PDF file.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Questionnaire uploaded.");
    } catch {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  return (
    <div>
      <div className="flex items-start gap-4">
        <span className="w-14 h-14 border border-black/10 bg-[#F9FAFB] flex items-center justify-center shrink-0">
          <FileText size={22} className="text-[#78be27]" />
        </span>
        <div className="flex-1 min-w-0">
          {value ? (
            <a
              href={resolveImg(value)}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono-accent text-sm truncate block hover:text-[#3da8d8]"
            >
              {value.split("/").pop()}
            </a>
          ) : (
            <p className="text-sm text-[#94A3B8] italic">No questionnaire uploaded yet.</p>
          )}
          <Input
            data-testid="cms-questionnaire-url"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="mt-2 rounded-none border-black/15"
            placeholder="/assets/…pdf or /api/files/…"
          />
          <label className="mt-2 inline-flex items-center gap-2 border border-black/15 px-3 py-2 cursor-pointer hover:border-[#78be27] font-mono-accent text-[10px] uppercase tracking-widest text-[#334155]">
            <Upload size={12} /> {uploading ? "Uploading…" : "Replace PDF"}
            <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={upload} data-testid="cms-questionnaire-file" />
          </label>
        </div>
      </div>
    </div>
  );
}

export default function ContentEditor() {
  const [content, setContent] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/content");
        setContent({ ...EMPTY, ...(data?.data || {}) });
      } catch {
        toast.error("Could not load content.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (k, v) => setContent((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", { data: content });
      toast.success("Site content saved.");
    } catch {
      toast.error("Failed to save content.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-10 text-[#52525B]">Loading content…</div>;

  return (
    <div className="space-y-8" data-testid="admin-content-editor">
      {/* Section headings — editable by the client */}
      <div className="border border-black/10 bg-white p-8" data-testid="cms-headings-panel">
        <h3 className="font-display font-semibold text-xl text-[#334155] mb-1">About — Section Headings</h3>
        <p className="text-xs text-[#52525B] mb-6">Rename any section title on the About page without a developer.</p>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Hero eyebrow</Label>
            <Input data-testid="cms-hero-eyebrow" value={content.hero_eyebrow || ""} onChange={(e) => set("hero_eyebrow", e.target.value)} className="mt-1 rounded-none border-black/15" />
          </div>
          <div>
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">MD section eyebrow</Label>
            <Input data-testid="cms-md-eyebrow" value={content.md_eyebrow || ""} onChange={(e) => set("md_eyebrow", e.target.value)} className="mt-1 rounded-none border-black/15" />
          </div>
          <div>
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Hero — line 1</Label>
            <Input data-testid="cms-hero-line1" value={content.hero_line1 || ""} onChange={(e) => set("hero_line1", e.target.value)} className="mt-1 rounded-none border-black/15" />
          </div>
          <div>
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Hero — line 2 <span className="text-[#94A3B8] normal-case">(last 2 words auto-highlighted)</span></Label>
            <Input data-testid="cms-hero-line2" value={content.hero_line2 || ""} onChange={(e) => set("hero_line2", e.target.value)} className="mt-1 rounded-none border-black/15" />
          </div>
          <div>
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">&quot;Our Story&quot; eyebrow</Label>
            <Input data-testid="cms-story-eyebrow" value={content.story_eyebrow || ""} onChange={(e) => set("story_eyebrow", e.target.value)} className="mt-1 rounded-none border-black/15" />
          </div>
          <div>
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">&quot;Our Story&quot; heading</Label>
            <Input data-testid="cms-story-heading" value={content.story_heading || ""} onChange={(e) => set("story_heading", e.target.value)} className="mt-1 rounded-none border-black/15" />
          </div>
          <div>
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">&quot;Our Policy&quot; eyebrow</Label>
            <Input data-testid="cms-policy-eyebrow" value={content.policy_eyebrow || ""} onChange={(e) => set("policy_eyebrow", e.target.value)} className="mt-1 rounded-none border-black/15" />
          </div>
          <div>
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">&quot;Our Values&quot; heading</Label>
            <Input data-testid="cms-values-heading" value={content.values_heading || ""} onChange={(e) => set("values_heading", e.target.value)} className="mt-1 rounded-none border-black/15" />
          </div>
          <div>
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">&quot;Awards&quot; eyebrow</Label>
            <Input data-testid="cms-awards-eyebrow" value={content.awards_eyebrow || ""} onChange={(e) => set("awards_eyebrow", e.target.value)} className="mt-1 rounded-none border-black/15" />
          </div>
          <div>
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">&quot;Awards&quot; heading</Label>
            <Input data-testid="cms-awards-heading" value={content.awards_heading || ""} onChange={(e) => set("awards_heading", e.target.value)} className="mt-1 rounded-none border-black/15" />
          </div>
          <div>
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">&quot;Patents&quot; eyebrow</Label>
            <Input data-testid="cms-patents-eyebrow" value={content.patents_eyebrow || ""} onChange={(e) => set("patents_eyebrow", e.target.value)} className="mt-1 rounded-none border-black/15" />
          </div>
          <div>
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">&quot;Patents&quot; heading</Label>
            <Input data-testid="cms-patents-heading" value={content.patents_heading || ""} onChange={(e) => set("patents_heading", e.target.value)} className="mt-1 rounded-none border-black/15" />
          </div>
        </div>
      </div>

      <div className="border border-black/10 bg-white p-8">
        <h3 className="font-display font-semibold text-xl text-[#334155] mb-6">About — Managing Director</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Name</Label>
            <Input data-testid="cms-md-name" value={content.md_name || ""} onChange={(e) => set("md_name", e.target.value)} className="mt-1 rounded-none border-black/15" />
          </div>
          <div>
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Role</Label>
            <Input data-testid="cms-md-role" value={content.md_role || ""} onChange={(e) => set("md_role", e.target.value)} className="mt-1 rounded-none border-black/15" />
          </div>
        </div>
        <div className="mt-4">
          <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Quote</Label>
          <Textarea data-testid="cms-md-quote" value={content.md_quote || ""} onChange={(e) => set("md_quote", e.target.value)} className="mt-1 rounded-none border-black/15 min-h-[90px]" />
        </div>
        <div className="mt-4">
          <ImageField label="MD Photo" value={content.md_photo} onChange={(v) => set("md_photo", v)} testid="cms-md-photo" />
        </div>
      </div>

      <div className="border border-black/10 bg-white p-8">
        <h3 className="font-display font-semibold text-xl text-[#334155] mb-6">About — Our Story</h3>
        {[1, 2, 3].map((n) => (
          <div key={n} className="mt-4 first:mt-0">
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Paragraph {n}</Label>
            <Textarea data-testid={`cms-story-p${n}`} value={content[`story_p${n}`] || ""} onChange={(e) => set(`story_p${n}`, e.target.value)} className="mt-1 rounded-none border-black/15 min-h-[100px]" />
          </div>
        ))}
      </div>

      <div className="border border-black/10 bg-white p-8">
        <h3 className="font-display font-semibold text-xl text-[#334155] mb-6">About — Our Policy</h3>
        <div>
          <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Policy Title</Label>
          <Input data-testid="cms-policy-title" value={content.policy_title || ""} onChange={(e) => set("policy_title", e.target.value)} className="mt-1 rounded-none border-black/15" />
        </div>
        <div className="mt-4">
          <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">
            Policy Body <span className="text-[#94A3B8] normal-case">— tabs, line breaks and empty lines are preserved on the site</span>
          </Label>
          <Textarea data-testid="cms-policy-body" value={content.policy_body || ""} onChange={(e) => set("policy_body", e.target.value)} className="mt-1 rounded-none border-black/15 min-h-[220px] font-mono text-sm whitespace-pre-wrap" />
        </div>
        <div className="mt-4">
          <ImageField label="Policy Image (QMS / Certificate)" value={content.policy_image} onChange={(v) => set("policy_image", v)} testid="cms-policy-image" />
        </div>
      </div>

      <div className="border border-black/10 bg-white p-8">
        <ListEditor label="Awards & Accolades" items={content.awards} onChange={(v) => set("awards", v)} testid="cms-award" />
      </div>

      <div className="border border-black/10 bg-white p-8">
        <ListEditor label="Patents" items={content.patents} onChange={(v) => set("patents", v)} testid="cms-patent" colorClass="text-[#3da8d8]" />
      </div>

      <div className="border border-black/10 bg-white p-8" data-testid="cms-questionnaire-panel">
        <h3 className="font-display font-semibold text-xl text-[#334155] mb-1">Contact — Questionnaire PDF</h3>
        <p className="text-xs text-[#52525B] mb-6">Powers the &quot;Download Questionnaire&quot; button on the Contact page. Upload a new PDF anytime to replace it.</p>
        <QuestionnaireField value={content.questionnaire_url} onChange={(v) => set("questionnaire_url", v)} />
      </div>

      <div className="sticky bottom-4 z-10">
        <button
          onClick={save}
          disabled={saving}
          data-testid="cms-save-btn"
          className="btn-fill w-full inline-flex items-center justify-center gap-3 border border-[#3da8d8] bg-white px-8 py-4 font-mono-accent text-xs uppercase tracking-widest text-[#3da8d8] hover:text-white disabled:opacity-50"
        >
          <Save size={14} /> {saving ? "Saving…" : "Save Site Content"}
        </button>
      </div>
    </div>
  );
}
